import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Wallet,
  ArrowDownToLine,
  X,
  Copy,
  Clock,
  User,
  TrendingUp,
  Target,
  Award,
  Search,
  Crown,
  Calendar,
  Eye,
  EyeOff,
  Trophy,
  BarChart3,
  CheckCircle,
} from "lucide-react";
import BottomNavbar from "../../components/navs/mobilenav";
import { useUserContext } from "../../context/userContext";
import toast from "react-hot-toast";
import confetti from "canvas-confetti";
import logo from "../../assets/mobile-logo.svg";
import Rbg from "../../assets/rbg.png";
import api from "../../utils/axiosConfig";

// PropTypes for all components
import PropTypes from "prop-types";

// Modal Components - Moved outside main component with display names and prop types
const UsernameSetupModal = React.memo(
  ({
    showUsernameSetup,
    username,
    phoneNumber,
    handleUsernameChange,
    handlePhoneNumberChange,
    handleSetupUsername,
    isSubmittingUsername,
    isCheckingUsername,
    usernameAvailability,
    usernameInputRef,
    needsPhoneNumber,
  }) => {
    return (
      <div
        className={`fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm transition-opacity duration-200 ${
          showUsernameSetup ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className={`fixed inset-0 flex items-center justify-center p-4 transition-transform duration-200 ${
            showUsernameSetup ? "scale-100" : "scale-95"
          }`}
        >
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex justify-center mb-4">
              <div className="bg-gradient-to-br from-[#2C0053] to-[#4a0080] p-4 rounded-3xl shadow-lg">
                <User className="text-white" size={32} />
              </div>
            </div>

            <h2 className="text-xl font-semibold text-center mb-2 text-gray-900">
              Complete Your Profile
            </h2>
            <p className="text-gray-600 text-center mb-4 text-xs">
              {needsPhoneNumber 
                ? "We need a few more details to get you started!" 
                : "Choose a unique username to start predicting!"}
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold mb-2 text-gray-700">
                  Username
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2C0053] font-semibold text-sm">
                    @
                  </span>
                  <input
                    ref={usernameInputRef}
                    type="text"
                    value={username}
                    onChange={handleUsernameChange}
                    placeholder="johndoe"
                    className={`w-full border-2 rounded-2xl p-3 pl-8 text-sm focus:outline-none transition-all shadow-sm ${
                      usernameAvailability === true
                        ? "border-green-500 focus:border-green-500"
                        : usernameAvailability === false
                        ? "border-red-500 focus:border-red-500"
                        : "border-gray-200 focus:border-[#2C0053]"
                    }`}
                    maxLength={20}
                  />
                  {isCheckingUsername && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#2C0053] border-t-transparent"></div>
                </div>
                  )}
                </div>
                
                {/* Username availability status */}
                {username.length >= 3 && !isCheckingUsername && usernameAvailability !== null && (
                  <div className={`text-xs mt-1 flex items-center gap-1 ${
                    usernameAvailability ? "text-green-600" : "text-red-600"
                  }`}>
                    {usernameAvailability ? (
                      <>
                        <CheckCircle size={10} />
                        Username is available
                      </>
                    ) : (
                      <>
                        <X size={10} />
                        Username is already taken
                      </>
                    )}
                  </div>
                )}
                
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <Target size={10} /> Only lowercase letters, numbers, and
                  underscores. Min 3 characters.
                </p>
              </div>

              {needsPhoneNumber && (
                <div>
                  <label className="block text-xs font-semibold mb-2 text-gray-700">
                    Phone Number
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                      +234
                    </span>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={handlePhoneNumberChange}
                      placeholder="8012345678"
                      className="w-full border-2 border-gray-200 rounded-2xl p-3 pl-14 text-sm focus:border-[#2C0053] outline-none transition-all shadow-sm"
                      maxLength={10}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <Target size={10} /> Enter your 10-digit phone number (without country code)
                  </p>
                </div>
              )}

              <button
                onClick={handleSetupUsername}
                disabled={
                  isSubmittingUsername || 
                  username.length < 3 || 
                  usernameAvailability === false || 
                  isCheckingUsername ||
                  (needsPhoneNumber && phoneNumber.length !== 10)
                }
                className="w-full bg-gradient-to-r from-[#2C0053] to-[#4a0080] text-white py-3 rounded-2xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmittingUsername ? "Setting up..." : isCheckingUsername ? "Checking..." : "Continue"}
              </button>
            </div>

            <div className="mt-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-3 border border-[#2C0053] border-opacity-20">
              <p className="text-xs text-[#2C0053] font-medium flex items-start gap-2">
                <Award size={14} className="flex-shrink-0 mt-0.5" />
                <span>
                  Your username will be visible on leaderboards and to other
                  players.
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

UsernameSetupModal.displayName = "UsernameSetupModal";

UsernameSetupModal.propTypes = {
  showUsernameSetup: PropTypes.bool.isRequired,
  username: PropTypes.string.isRequired,
  phoneNumber: PropTypes.string.isRequired,
  handleUsernameChange: PropTypes.func.isRequired,
  handlePhoneNumberChange: PropTypes.func.isRequired,
  handleSetupUsername: PropTypes.func.isRequired,
  isSubmittingUsername: PropTypes.bool.isRequired,
  isCheckingUsername: PropTypes.bool.isRequired,
  usernameAvailability: PropTypes.bool,
  usernameInputRef: PropTypes.object.isRequired,
  needsPhoneNumber: PropTypes.bool.isRequired,
};

const WalletModal = React.memo(
  ({
    showWalletModal,
    setShowWalletModal,
    showBalance,
    toggleBalanceVisibility,
    handleCopyVirtualAccount,
    searchQuery,
    setSearchQuery,
    filteredTransactions,
    user,
    loadingTransactions,
    onWithdrawClick,
  }) => {
    return (
      <div
        className={`fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity duration-200 ${
          showWalletModal ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setShowWalletModal(false)}
      >
        <div
          className={`fixed inset-0 bg-white md:w-xl transition-transform duration-200 overflow-y-auto ${
            showWalletModal ? "translate-x-0" : "translate-x-full"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0  bg-gradient-to-br from-[#2C0053] to-[#4a0080] text-white p-4 pb-8 shadow-xl z-10">
            <div className="flex justify-between items-center mb-6">
              <h2 className="md:text-xl font-semibold flex items-center gap-2 text-sm">
                <Wallet size={20} /> My Wallet
              </h2>
              <button
                onClick={() => setShowWalletModal(false)}
                className="bg-white/20 hover:bg-white/30 p-1 rounded-xl"
              >
                <X size={18} />
              </button>
            </div>

            <div className="text-center bg-white/10 backdrop-blur-md rounded-3xl p-4 border border-white/20">
              <div className="flex items-center justify-center gap-2 mb-1">
                <p className="text-xs opacity-90 flex items-center gap-1">
                  <TrendingUp size={12} /> Available Balance
                </p>
                <button
                  onClick={toggleBalanceVisibility}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  {showBalance ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
              </div>
              <h3 className="text-2xl font-semibold">
                {showBalance ? `₦${(user?.user?.walletBalance || 0).toLocaleString()}` : "xxxxxx"}
              </h3>
            </div>
          </div>

          <div className="p-4 space-y-4 pb-8">
            <div>
              <button 
                onClick={onWithdrawClick}
                className="w-full bg-gradient-to-br from-[#2C0053] to-[#4a0080] text-white py-3 rounded-2xl font-semibold flex items-center justify-center gap-2 text-sm shadow-lg hover:shadow-xl transition-all"
              >
                <ArrowDownToLine size={18} /> Withdraw
              </button>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-4 border border-gray-200 shadow-sm">
              <p className="text-xs text-gray-500 mb-3 font-semibold uppercase tracking-wide flex items-center gap-1">
                <Copy size={10} /> Fund Your Wallet
              </p>
              <div className="space-y-2">
                <div className="flex justify-between items-center py-1 border-b border-gray-200">
                  <p className="text-xs text-gray-500 font-medium">
                    {user?.user?.dedicatedAccountBank}
                  </p>
                  <div className="flex items-center gap-1">
                    <p className="font-semibold text-gray-900 text-sm">
                      {user?.user?.dedicatedAccountNumber}
                    </p>
                    <button
                      onClick={handleCopyVirtualAccount}
                      className="bg-[#2C0053]/10 text-[#2C0053] p-1 rounded-lg"
                    >
                      <Copy size={12} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="md:text-base font-semibold text-gray-900 flex items-center gap-2 text-sm">
                  <Clock size={16} className="text-[#2C0053]" /> Transaction
                  History
                </h3>
              </div>

              <div className="relative mb-3">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={14}
                />
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 text-sm border-2 border-gray-200 rounded-2xl focus:border-[#2C0053] outline-none transition-all shadow-sm"
                />
              </div>

              <div className="max-h-96 overflow-y-auto space-y-2 pr-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 pb-8">
                {loadingTransactions ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2C0053] mx-auto mb-2"></div>
                    <p className="text-sm text-gray-500">Loading transactions...</p>
                  </div>
                ) : filteredTransactions.length ? (
                  filteredTransactions.map((transaction) => (
                    <div
                      key={transaction.id}
                      className="flex items-center justify-between p-3 bg-white rounded-2xl border border-gray-200 shadow-sm"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`p-2 rounded-xl shadow-sm ${
                            transaction.type === "win"
                              ? "bg-green-100"
                              : transaction.type === "deposit"
                              ? "bg-blue-100"
                              : transaction.type === "withdrawal"
                              ? "bg-red-100"
                              : "bg-gray-100"
                          }`}
                        >
                          {transaction.type === "win" && (
                            <span className="text-green-600 text-sm">🏆</span>
                          )}
                          {transaction.type === "deposit" && (
                            <ArrowDownToLine
                              className="text-blue-600"
                              size={14}
                            />
                          )}
                          {transaction.type === "withdrawal" && (
                            <ArrowDownToLine
                              className="text-red-600"
                              size={14}
                            />
                          )}
                          {transaction.type === "entry" && (
                            <Wallet className="text-gray-600" size={14} />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-xs text-gray-900">
                            {transaction.description}
                          </p>
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                            <Clock size={8} />
                            {transaction.date}
                          </p>
                        </div>
                      </div>
                      <p
                        className={`font-semibold text-sm ${
                          transaction.amount > 0
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {transaction.amount > 0 ? "+" : ""}₦
                        {Math.abs(transaction.amount).toLocaleString()}
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <Search size={32} className="mx-auto mb-1 opacity-50" />
                    <p className="text-sm">No transactions found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

WalletModal.displayName = "WalletModal";

WalletModal.propTypes = {
  showWalletModal: PropTypes.bool.isRequired,
  setShowWalletModal: PropTypes.func.isRequired,
  showBalance: PropTypes.bool.isRequired,
  toggleBalanceVisibility: PropTypes.func.isRequired,
  handleCopyVirtualAccount: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  setSearchQuery: PropTypes.func.isRequired,
  filteredTransactions: PropTypes.array.isRequired,
  user: PropTypes.object.isRequired,
  loadingTransactions: PropTypes.bool.isRequired,
  onWithdrawClick: PropTypes.func.isRequired,
};

const WithdrawalModal = React.memo(
  ({
    showWithdrawalModal,
    setShowWithdrawalModal,
    withdrawalAmount,
    setWithdrawalAmount,
    handleWithdrawal,
    isWithdrawing,
    user,
  }) => {
    return (
      <div
        className={`fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity duration-200 flex items-center justify-center p-4 ${
          showWithdrawalModal ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => !isWithdrawing && setShowWithdrawalModal(false)}
      >
        <div
          className={`bg-white rounded-3xl shadow-2xl max-w-md w-full transition-all duration-200 ${
            showWithdrawalModal ? "scale-100" : "scale-95"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-[#2C0053] to-[#4a0080] text-white p-6 rounded-t-3xl">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <ArrowDownToLine size={24} /> Withdraw Funds
              </h2>
              <button
                onClick={() => !isWithdrawing && setShowWithdrawalModal(false)}
                disabled={isWithdrawing}
                className="bg-white/20 hover:bg-white/30 p-2 rounded-xl transition-all disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-sm opacity-90">
              Available Balance: ₦{(user?.user?.walletBalance || 0).toLocaleString()}
            </p>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Withdrawal Amount
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                  ₦
                </span>
                <input
                  type="number"
                  value={withdrawalAmount}
                  onChange={(e) => setWithdrawalAmount(e.target.value)}
                  placeholder="Enter amount"
                  disabled={isWithdrawing}
                  className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-2xl focus:border-[#2C0053] outline-none transition-all text-lg font-semibold disabled:bg-gray-100"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Minimum withdrawal: ₦100
              </p>
            </div>

            {/* Quick Amount Buttons */}
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2">Quick Select:</p>
              <div className="grid grid-cols-3 gap-2">
                {[1000, 5000, 10000].map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setWithdrawalAmount(amount.toString())}
                    disabled={isWithdrawing || (user?.user?.walletBalance || 0) < amount}
                    className="py-2 px-3 bg-gray-100 hover:bg-[#2C0053]/10 border-2 border-gray-200 hover:border-[#2C0053] rounded-xl text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ₦{amount.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowWithdrawalModal(false)}
                disabled={isWithdrawing}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-2xl font-semibold hover:bg-gray-300 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleWithdrawal}
                disabled={isWithdrawing || !withdrawalAmount}
                className="flex-1 bg-gradient-to-r from-[#2C0053] to-[#4a0080] text-white py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isWithdrawing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <ArrowDownToLine size={18} />
                    Withdraw
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

WithdrawalModal.displayName = "WithdrawalModal";

WithdrawalModal.propTypes = {
  showWithdrawalModal: PropTypes.bool.isRequired,
  setShowWithdrawalModal: PropTypes.func.isRequired,
  withdrawalAmount: PropTypes.string.isRequired,
  setWithdrawalAmount: PropTypes.func.isRequired,
  handleWithdrawal: PropTypes.func.isRequired,
  isWithdrawing: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
};

const PredictModal = React.memo(
  ({
    showPredictModal,
    setShowPredictModal,
    selectedMatch,
    showPredictionConfirmation,
    homeScore,
    awayScore,
    confirmedHomeScore,
    confirmedAwayScore,
    handleScoreChange,
    handleSubmitPrediction,
    handleEditPrediction,
    predictionStatus,
  }) => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
      const checkMobile = () => setIsMobile(window.innerWidth < 768);
      checkMobile();
      window.addEventListener("resize", checkMobile);
      return () => window.removeEventListener("resize", checkMobile);
    }, []);

    if (!selectedMatch) return null;

    const isSubmitted = predictionStatus[selectedMatch.id] === "submitted";
    const isWon = predictionStatus[selectedMatch.id] === "won";

    return (
      <div
        className={`fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity duration-200 ${
          showPredictModal ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setShowPredictModal(false)}
      >
        {/* Responsive container - Center on desktop, bottom sheet on mobile */}
        <div
          className={`${
            isMobile
              ? "fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[95vh] overflow-y-auto"
              : "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-3xl max-h-[90vh] overflow-y-auto w-full max-w-2xl"
          } transition-all duration-200 ${
            showPredictModal
              ? isMobile
                ? "translate-y-0"
                : "scale-100 opacity-100"
              : isMobile
              ? "translate-y-full"
              : "scale-95 opacity-0"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 pt-6 pb-4 z-10">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Target size={24} className="text-[#2C0053]" />
                {showPredictionConfirmation
                  ? "Confirm Prediction"
                  : isWon
                  ? "Prediction Won! 🎉"
                  : isSubmitted
                  ? "Submitted"
                  : "Enter Your Prediction"}
              </h2>
              <button
                onClick={() => setShowPredictModal(false)}
                className="bg-gray-100 hover:bg-gray-200 p-2 rounded-xl"
              >
                <X className="text-gray-600" size={20} />
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-6 space-y-6 pb-10">
            <div className="flex items-center justify-center gap-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-5 border border-gray-200">
              <div className="text-center">
                <img
                  src={selectedMatch.homeLogo}
                  alt={selectedMatch.homeTeam}
                  className="w-14 h-14 rounded-2xl mx-auto mb-2 border-2 border-white shadow-md"
                />
                <p className="text-xs font-semibold text-gray-900">
                  {selectedMatch.homeTeam}
                </p>
              </div>
              <div className="bg-white rounded-xl px-3 py-2 shadow-sm">
                <span className="text-gray-400 font-semibold">VS</span>
              </div>
              <div className="text-center">
                <img
                  src={selectedMatch.awayLogo}
                  alt={selectedMatch.awayTeam}
                  className="w-14 h-14 rounded-2xl mx-auto mb-2 border-2 border-white shadow-md"
                />
                <p className="text-xs font-semibold text-gray-900">
                  {selectedMatch.awayTeam}
                </p>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="flex items-center gap-2 bg-white rounded-2xl px-4 py-2 border border-gray-200 shadow-sm">
                <img
                  src={selectedMatch.leagueLogo}
                  alt={selectedMatch.league}
                  className="w-6 h-6 rounded-full"
                />
                <span className="text-xs font-semibold text-gray-700">
                  {selectedMatch.league}
                </span>
              </div>
            </div>

            {isWon ? (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200 shadow-sm text-center">
                <div className="flex justify-center mb-4">
                  <div className="bg-green-500 p-3 rounded-full">
                    <CheckCircle className="text-white" size={32} />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-green-800 mb-2">
                  Congratulations! 🎉
                </h3>
                <p className="text-green-600 mb-4">
                  Your prediction was correct and you won!
                </p>
                <div className="bg-white rounded-2xl p-4 border border-green-300">
                  <p className="text-sm text-gray-600 mb-2">Your Prediction</p>
                  <div className="flex items-center justify-center gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">
                        {confirmedHomeScore}
                      </p>
                    </div>
                    <div className="text-xl font-semibold text-gray-400">-</div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">
                        {confirmedAwayScore}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : !showPredictionConfirmation ? (
              <div className="flex items-center justify-center gap-4">
                <div className="flex-1">
                  <label className="block text-center text-sm font-semibold mb-3 text-gray-700">
                    {selectedMatch.homeTeam}
                  </label>
                  <input
                    type="number"
                    value={homeScore}
                    onChange={(e) => handleScoreChange("home", e.target.value)}
                    placeholder="0"
                    className="w-full text-center text-4xl font-semibold border-2 border-gray-300 rounded-2xl p-5 focus:outline-none focus:ring-4 focus:ring-[#2C0053]/20 focus:border-[#2C0053] shadow-lg transition-all"
                    min="0"
                    disabled={isSubmitted}
                  />
                </div>

                <span className="text-3xl font-semibold text-gray-400 mt-10">
                  -
                </span>

                <div className="flex-1">
                  <label className="block text-center text-sm font-semibold mb-3 text-gray-700">
                    {selectedMatch.awayTeam}
                  </label>
                  <input
                    type="number"
                    value={awayScore}
                    onChange={(e) => handleScoreChange("away", e.target.value)}
                    placeholder="0"
                    className="w-full text-center text-4xl font-semibold border-2 border-gray-300 rounded-2xl p-5 focus:outline-none focus:ring-4 focus:ring-[#2C0053]/20 focus:border-[#2C0053] shadow-lg transition-all"
                    min="0"
                    disabled={isSubmitted}
                  />
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200 shadow-sm">
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold text-green-800">
                    Confirm Your Prediction
                  </h3>
                  <p className="text-sm text-green-600">
                    Please review your prediction before submitting
                  </p>
                </div>

                <div className="flex items-center justify-center gap-6 bg-white rounded-2xl p-4 border border-green-300">
                  <div className="text-center">
                    <p className="text-xs text-gray-600 mb-1">
                      {selectedMatch.homeTeam}
                    </p>
                    <p className="text-4xl font-semibold text-gray-900">
                      {confirmedHomeScore}
                    </p>
                  </div>

                  <div className="text-2xl font-semibold text-gray-400">-</div>

                  <div className="text-center">
                    <p className="text-xs text-gray-600 mb-1">
                      {selectedMatch.awayTeam}
                    </p>
                    <p className="text-4xl font-semibold text-gray-900">
                      {confirmedAwayScore}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 mt-4">
                  <button
                    onClick={handleEditPrediction}
                    className="flex-1 bg-gray-500 text-white text-sm py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all"
                  >
                    Edit
                  </button>
                  <button
                    onClick={handleSubmitPrediction}
                    className="flex-1 bg-gradient-to-r text-sm from-green-500 to-emerald-600 text-white py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all"
                  >
                    Confirm & Submit
                  </button>
                </div>
              </div>
            )}

            {/* Only show entry fee and potential win if prediction is not won */}
            {!isWon && (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-5 border border-[#2C0053]/20 shadow-sm">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-xs text-gray-600 font-semibold flex items-center gap-1">
                    <Wallet size={16} className="text-red-500" /> Entry Fee:
                  </span>
                  <span className="font-semibold text-red-600 text-sm">
                    -₦{selectedMatch.entryFee.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600 font-semibold flex items-center gap-1">
                    <TrendingUp size={16} className="text-green-600" />{" "}
                    Potential Win:
                  </span>
                  <span className="font-semibold text-green-600 text-sm">
                    +₦{selectedMatch.potentialWin.toLocaleString()}
                  </span>
                </div>
              </div>
            )}

            {!showPredictionConfirmation && !isWon && !isSubmitted && (
              <button
                onClick={handleSubmitPrediction}
                disabled={homeScore === "" || awayScore === ""}
                className="w-full bg-gradient-to-r from-[#2C0053] to-[#4a0080] text-white py-3 rounded-2xl font-semibold md:text-lg shadow-lg mb-8 hover:shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Prediction
              </button>
            )}

            {isSubmitted && !isWon && (
              <div className="text-center py-4">
                <div className="flex items-center justify-center gap-2 text-orange-600 mb-2">
                  <Clock size={20} />
                  <span className="font-semibold">Submitted</span>
                </div>
                <p className="text-sm text-gray-600">
                  Waiting for match results...
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

PredictModal.displayName = "PredictModal";

PredictModal.propTypes = {
  showPredictModal: PropTypes.bool.isRequired,
  setShowPredictModal: PropTypes.func.isRequired,
  selectedMatch: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    homeLogo: PropTypes.string.isRequired,
    homeTeam: PropTypes.string.isRequired,
    awayLogo: PropTypes.string.isRequired,
    awayTeam: PropTypes.string.isRequired,
    leagueLogo: PropTypes.string.isRequired,
    league: PropTypes.string.isRequired,
    entryFee: PropTypes.number.isRequired,
    potentialWin: PropTypes.number.isRequired,
  }),
  showPredictionConfirmation: PropTypes.bool.isRequired,
  homeScore: PropTypes.string.isRequired,
  awayScore: PropTypes.string.isRequired,
  confirmedHomeScore: PropTypes.string.isRequired,
  confirmedAwayScore: PropTypes.string.isRequired,
  handleScoreChange: PropTypes.func.isRequired,
  handleSubmitPrediction: PropTypes.func.isRequired,
  handleEditPrediction: PropTypes.func.isRequired,
  predictionStatus: PropTypes.object.isRequired,
};

const LeaderboardModal = React.memo(
  ({ showLeaderboard, setShowLeaderboard, leaderboardData, pastWinners, loadingLeaderboard }) => {
    return (
      <div
        className={`fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity duration-200 ${
          showLeaderboard ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setShowLeaderboard(false)}
      >
        <div
          className={`fixed inset-0 bg-white transition-transform duration-200 overflow-y-auto ${
            showLeaderboard ? "translate-x-0" : "translate-x-full"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-gradient-to-br from-[#2C0053] to-[#4a0080] text-white p-6 pb-10 shadow-xl z-10">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-2xl font-semibold flex items-center gap-2">
                <Crown size={28} /> Leaderboard
              </h2>
              <button
                onClick={() => setShowLeaderboard(false)}
                className="bg-white/20 hover:bg-white/30 p-2 rounded-xl"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6 pb-10">
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-900">
                <span className="text-yellow-500 text-xl">🏆</span> Top Winners
              </h3>
              
              {loadingLeaderboard ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2C0053] mx-auto mb-2"></div>
                  <p className="text-sm text-gray-500">Loading leaderboard...</p>
                </div>
              ) : leaderboardData.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-2xl border border-gray-200">
                  <Crown size={48} className="mx-auto text-gray-400 mb-3" />
                  <p className="text-sm font-semibold text-gray-600 mb-1">No Winners Yet</p>
                  <p className="text-xs text-gray-500">Be the first to win and top the leaderboard!</p>
                </div>
              ) : (
              <div className="space-y-3">
                {leaderboardData.slice(0, 3).map((user, index) => (
                  <div
                    key={user.rank}
                    className={`flex items-center justify-between p-3 rounded-2xl border shadow-sm ${
                      index === 0
                        ? "bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200"
                        : index === 1
                        ? "bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200"
                        : "bg-gradient-to-r from-orange-50 to-red-50 border-orange-200"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-white ${
                          index === 0
                            ? "bg-yellow-500"
                            : index === 1
                            ? "bg-gray-500"
                            : "bg-orange-500"
                        }`}
                      >
                        {index + 1}
                      </div>
                      <div>
                        <p className=" text-[14px] text-gray-900">
                          @{user.username}
                        </p>
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <Calendar size={10} />
                          {user.date}
                        </p>
                      </div>
                    </div>
                      <div className="text-right">
                    <p className="font-semibold text-green-600 text-xs md:text-sm">
                      ₦{user.amount.toLocaleString()}
                    </p>
                        <p className="text-xs text-gray-500">{user.wins} wins</p>
                      </div>
                  </div>
                ))}
              </div>
              )}
            </div>

            {leaderboardData.length > 3 && (
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-900">
                  <Trophy size={20} className="text-[#2C0053]" /> All Rankings
                </h3>
                <div className="space-y-2 max-h-96 overflow-y-auto pb-4">
                  {leaderboardData.slice(3).map((user) => (
                    <div
                      key={user.rank}
                      className="flex items-center justify-between p-3 bg-white rounded-2xl border border-gray-200 shadow-sm hover:border-[#2C0053]/30 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center font-semibold text-white bg-gray-400 text-sm">
                          {user.rank}
                        </div>
                        <div>
                          <p className="text-[14px] text-gray-900">
                            @{user.username}
                          </p>
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <Calendar size={10} />
                            {user.date}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-semibold text-green-600">
                          ₦{user.amount.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">{user.wins} wins</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

LeaderboardModal.displayName = "LeaderboardModal";

LeaderboardModal.propTypes = {
  showLeaderboard: PropTypes.bool.isRequired,
  setShowLeaderboard: PropTypes.func.isRequired,
  leaderboardData: PropTypes.array.isRequired,
  pastWinners: PropTypes.array.isRequired,
  loadingLeaderboard: PropTypes.bool.isRequired,
};

const WinModal = React.memo(({ showWinModal, setShowWinModal, winAmount }) => {
  return (
    <div
      className={`fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm transition-opacity duration-200 ${
        showWinModal ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        className={`fixed inset-0 flex items-center justify-center p-4 transition-transform duration-200 ${
          showWinModal ? "scale-100" : "scale-95"
        }`}
      >
        <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-gradient-to-br from-yellow-400 to-orange-500 p-6 rounded-3xl shadow-lg animate-pulse">
              <span className="text-4xl">🏆</span>
            </div>
          </div>

          <h2 className="text-2xl md:text-3xl font-semibold text-center mb-4 text-gray-900">
            Congratulations!
          </h2>
          <p className="text-gray-600 text-center mb-2">You&apos;ve won</p>
          <p className="text-4xl font-semibold text-green-600 mb-6">
            ₦{winAmount.toLocaleString()}
          </p>

          <p className="text-sm text-gray-500 mb-6">
            Your winnings have been credited to your wallet. Keep predicting to
            win more!
          </p>

          <button
            onClick={() => setShowWinModal(false)}
            className="w-full bg-gradient-to-r from-[#2C0053] to-[#4a0080] text-white py-4 rounded-2xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
});

WinModal.displayName = "WinModal";

WinModal.propTypes = {
  showWinModal: PropTypes.bool.isRequired,
  setShowWinModal: PropTypes.func.isRequired,
  winAmount: PropTypes.number.isRequired,
};

const Marquee = React.memo(({ marqueeWinners }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // useEffect must be here - at the top level before any conditional returns
  useEffect(() => {
    if (marqueeWinners.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % marqueeWinners.length);
    }, 4000); // Changed to 4 seconds for better readability

    return () => clearInterval(interval);
  }, [marqueeWinners.length]);

  // Props validation - this can stay here since it's not a hook
  if (!marqueeWinners || !Array.isArray(marqueeWinners)) {
    return null;
  }

  // If no winners, show default message
  if (marqueeWinners.length === 0) {
    return (
      <div className="relative bottom-5 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 mb-6 shadow-lg overflow-hidden">
        <div className="flex items-center justify-center gap-2 animate-pulse">
          <span className="text-sm">🏆</span>
          <span className="text-xs font-semibold truncate">
            You can be a lucky winner today!
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bottom-5 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 mb-6 shadow-lg overflow-hidden">
      <div className="flex items-center justify-center gap-2">
        <span className="text-sm">🏆</span>
        <span className="text-xs font-semibold truncate transition-all duration-500">
          {marqueeWinners[currentIndex]?.displayText}
        </span>
        {marqueeWinners.length > 1 && (
          <span className="text-xs opacity-70 ml-2">
            ({currentIndex + 1}/{marqueeWinners.length})
          </span>
        )}
      </div>
    </div>
  );
});

Marquee.displayName = "Marquee";

Marquee.propTypes = {
  marqueeWinners: PropTypes.arrayOf(
    PropTypes.shape({
      displayText: PropTypes.string.isRequired,
    })
  ).isRequired,
};

// Prediction Button Component
const PredictionButton = React.memo(({ status, onClick, className = "" }) => {
  const getButtonProps = () => {
    switch (status) {
      case "won":
        return {
          className: `bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-2 rounded-2xl font-semibold text-sm shadow-lg cursor-default ${className}`,
          children: (
            <>
              <CheckCircle size={16} className="inline mr-1" />
              Won! 🎉
            </>
          ),
          disabled: true,
        };
      case "lost":
        return {
          className: `bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-2 rounded-2xl font-semibold text-sm shadow-lg cursor-default ${className}`,
          children: (
            <>
              <X size={16} className="inline mr-1" />
              Lost
            </>
          ),
          disabled: true,
        };
      case "submitted":
        return {
          className: `bg-gray-400 text-white px-6 py-2 rounded-2xl font-semibold text-sm shadow-lg cursor-default ${className}`,
          children: "Submitted",
          disabled: true,
        };
      default:
        return {
          className: `bg-gradient-to-r from-[#2C0053] to-[#4a0080] text-white px-6 py-2 rounded-2xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all ${className}`,
          children: "Predict",
          onClick: onClick,
          disabled: false,
        };
    }
  };

  const buttonProps = getButtonProps();

  return <button {...buttonProps}>{buttonProps.children}</button>;
});

PredictionButton.displayName = "PredictionButton";

PredictionButton.propTypes = {
  status: PropTypes.oneOf(["won", "lost", "submitted", "default"]),
  onClick: PropTypes.func,
  className: PropTypes.string,
};

export default function PredictWinDashboard() {
  const { user } = useUserContext();
  // console.log(user)

  // UI state
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showPredictModal, setShowPredictModal] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showWinModal, setShowWinModal] = useState(false);
  const [showUsernameSetup, setShowUsernameSetup] = useState(false);
  const [showBalance, setShowBalance] = useState(true);
  const [showPredictionConfirmation, setShowPredictionConfirmation] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);

  // Form state
  const [username, setUsername] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [needsPhoneNumber, setNeedsPhoneNumber] = useState(false);
  const [isSubmittingUsername, setIsSubmittingUsername] = useState(false);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameAvailability, setUsernameAvailability] = useState(null); // null, true, false
  const [searchQuery, setSearchQuery] = useState("");
  const [homeScore, setHomeScore] = useState("");
  const [awayScore, setAwayScore] = useState("");
  const [confirmedHomeScore, setConfirmedHomeScore] = useState("");
  const [confirmedAwayScore, setConfirmedAwayScore] = useState("");
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  // Data state
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [winAmount, setWinAmount] = useState(0);
  const [predictionStatus, setPredictionStatus] = useState({}); // 'submitted', 'won', or undefined
  const [userDisplayName, setUserDisplayName] = useState("johndoe"); // Track username
  const [marqueeWinners, setMarqueeWinners] = useState([]); // Track marquee winners

  // User stats state
  const [userStats, setUserStats] = useState({
    totalWinnings: 0,
    totalPredictions: 0,
    wins: 0,
    winRate: 0,
  });
  const [loadingStats, setLoadingStats] = useState(false);

  // Refs
  const usernameInputRef = useRef(null);
  const usernameCheckTimeoutRef = useRef(null);

  // Today's match data from API
  const [todaysMatch, setTodaysMatch] = useState(null);
  const [loadingMatch, setLoadingMatch] = useState(true);

  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // Leaderboard state
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);

  // Calculate slots remaining
  const slotsRemaining = todaysMatch
    ? todaysMatch.maxParticipants - todaysMatch.currentParticipants
    : 0;

  const pastWinners = useMemo(
    () => [
      {
        username: "champion1",
        match: "Chelsea 2-1 Arsenal",
        amount: 5000,
        date: "Yesterday",
      },
      {
        username: "goalgetter",
        match: "Man Utd 3-0 Spurs",
        amount: 7500,
        date: "2 days ago",
      },
    ],
    []
  );

  // Filtered transactions based on search
  const filteredTransactions = useMemo(() => {
    if (!searchQuery) return transactions;
    return transactions.filter((transaction) =>
      transaction.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [transactions, searchQuery]);

  // Fetch user transactions
  useEffect(() => {
    const fetchTransactions = async () => {
      if (!user) return;
      
      setLoadingTransactions(true);
      try {
        const response = await api.get('/wallet/transactions');
        const { transactions: fetchedTransactions } = response.data;
        
        // Transform transactions to match UI format
        const transformedTransactions = fetchedTransactions.map(transaction => {
          // Calculate time difference
          const getTimeAgo = (date) => {
            const now = new Date();
            const transactionDate = new Date(date);
            const diffInMs = now - transactionDate;
            const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
            const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
            const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
            
            if (diffInMinutes < 60) {
              return diffInMinutes === 0 ? 'Just now' : `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
            } else if (diffInHours < 24) {
              return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
            } else if (diffInDays < 7) {
              return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
            } else {
              return transactionDate.toLocaleDateString('en-GB', { 
                day: 'numeric', 
                month: 'short', 
                year: 'numeric' 
              });
            }
          };

          // Map transaction type to UI type
          const typeMapping = {
            'wallet_funding': 'deposit',
            'prediction_debit': 'entry',
            'prediction_win': 'win',
            'withdrawal': 'withdrawal'
          };

          return {
            id: transaction.id || transaction._id,
            type: typeMapping[transaction.type] || transaction.type,
            amount: transaction.type === 'prediction_debit' || transaction.type === 'withdrawal' 
              ? -Math.abs(transaction.amount) 
              : transaction.amount,
            description: transaction.description,
            date: getTimeAgo(transaction.createdAt),
            status: transaction.status,
            balanceBefore: transaction.balanceBefore,
            balanceAfter: transaction.balanceAfter,
            metadata: transaction.metadata
          };
        });
        
        setTransactions(transformedTransactions);
      } catch (error) {
        console.error('Error fetching transactions:', error);
        toast.error('Unable to load transaction history');
      } finally {
        setLoadingTransactions(false);
      }
    };

    fetchTransactions();
  }, [user]);

  // Fetch user prediction stats
  useEffect(() => {
    const fetchPredictionStats = async () => {
      if (!user) return;
      
      setLoadingStats(true);
      try {
        const response = await api.get('/wallet/prediction-stats');
        const { totalPredictions, totalWins, totalWinnings, winRate } = response.data;
        
        setUserStats({
          totalWinnings: totalWinnings || 0,
          totalPredictions: totalPredictions || 0,
          wins: totalWins || 0,
          winRate: parseFloat(winRate) || 0,
        });
      } catch (error) {
        console.error('Error fetching prediction stats:', error);
        // Don't show error toast for stats, just keep default values
      } finally {
        setLoadingStats(false);
      }
    };

    fetchPredictionStats();
  }, [user]);

  // Fetch leaderboard
  useEffect(() => {
    const fetchLeaderboard = async () => {
      if (!user) return;
      
      setLoadingLeaderboard(true);
      try {
        const response = await api.get('/wallet/leaderboard');
        const { leaderboard } = response.data;
        
        // Transform leaderboard data to match UI format
        const transformedLeaderboard = leaderboard.map(entry => {
          // Format last win date
          const formatDate = (dateString) => {
            const date = new Date(dateString);
            const now = new Date();
            const diffInMs = now - date;
            const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
            const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
            
            if (diffInHours < 24) {
              return 'Today';
            } else if (diffInDays === 1) {
              return 'Yesterday';
            } else if (diffInDays < 7) {
              return `${diffInDays} days ago`;
            } else {
              return date.toLocaleDateString('en-GB', { 
                day: 'numeric', 
                month: 'short' 
              });
            }
          };

          return {
            rank: entry.rank,
            username: entry.username,
            amount: entry.totalWinnings,
            wins: entry.totalWins,
            date: formatDate(entry.lastWinDate)
          };
        });
        
        setLeaderboardData(transformedLeaderboard);
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
        // Don't show error toast, just keep empty leaderboard
      } finally {
        setLoadingLeaderboard(false);
      }
    };

    fetchLeaderboard();
  }, [user]);

  useEffect(() => {
    console.log('User context:', user);
    console.log('Username:', user?.user?.username);
    console.log('Phone:', user?.user?.phone);
    
    if (user) {
      const hasUsername = !!user?.user?.username;
      // Check if phone exists and is not an empty string
      const hasPhone = !!user?.user?.phone && user?.user?.phone !== "";
      
      if (!hasUsername) {
        // User exists but has no username - show modal
        setNeedsPhoneNumber(!hasPhone);
      setShowUsernameSetup(true);
      } else {
        // User has a username - hide modal and set display name
        setShowUsernameSetup(false);
        setUserDisplayName(user.user.username);
      }
    }
  }, [user]);

  // Fetch today's match
  useEffect(() => {
    const fetchTodaysMatch = async () => {
      setLoadingMatch(true);
      try {
        const response = await api.get('/predict/today');
        const { predictGame } = response.data;
        
        if (predictGame) {
          // Check if current user has made a prediction
          const currentUserId = user?.user?._id;
          const userParticipant = predictGame.participants.find(
            p => p.user._id === currentUserId
          );

          // Transform the API response to match the expected format
          const transformedMatch = {
            id: predictGame._id,
            fixtureId: predictGame.fixture.fixtureId,
            homeTeam: predictGame.fixture.homeTeam,
            awayTeam: predictGame.fixture.awayTeam,
            homeLogo: predictGame.fixture.homeTeamEmblem,
            awayLogo: predictGame.fixture.awayTeamEmblem,
            date: new Date(predictGame.fixture.date).toISOString().split('T')[0],
            time: new Date(predictGame.fixture.date).toLocaleTimeString('en-GB', { 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: false 
            }),
            potentialWin: predictGame.potentialWin,
            entryFee: predictGame.participationFee,
            league: predictGame.fixture.league,
            leagueLogo: predictGame.fixture.leagueEmblem,
            currentParticipants: predictGame.participants.length,
            maxParticipants: predictGame.maxParticipants,
            slotsRemaining: predictGame.maxParticipants - predictGame.participants.length,
            played: predictGame.fixture.played,
            resultPrediction: predictGame.fixture.resultPrediction,
            overUnderPrediction: predictGame.fixture.overUnderPrediction,
            ggngPrediction: predictGame.fixture.ggngPrediction,
          };
          
          setTodaysMatch(transformedMatch);

          // Update prediction status based on user's participation
          if (userParticipant) {
            if (userParticipant.won) {
              // User has won - show win modal
              setPredictionStatus((prev) => ({
                ...prev,
                [predictGame._id]: "won",
              }));
              setWinAmount(predictGame.potentialWin);
              setConfirmedHomeScore(userParticipant.homePrediction.toString());
              setConfirmedAwayScore(userParticipant.awayPrediction.toString());
              setShowWinModal(true);
              
              // Trigger confetti
              confetti({
                particleCount: 150,
                spread: 70,
                origin: { y: 0.6 },
              });
            } else if (predictGame.fixture.played) {
              // Game has been played and user didn't win - user lost
              setPredictionStatus((prev) => ({
                ...prev,
                [predictGame._id]: "lost",
              }));
              setConfirmedHomeScore(userParticipant.homePrediction.toString());
              setConfirmedAwayScore(userParticipant.awayPrediction.toString());
            } else {
              // User has submitted a prediction but game hasn't been played yet
              setPredictionStatus((prev) => ({
                ...prev,
                [predictGame._id]: "submitted",
              }));
              setConfirmedHomeScore(userParticipant.homePrediction.toString());
              setConfirmedAwayScore(userParticipant.awayPrediction.toString());
            }
          }

          // Populate marquee with winners
          const winners = predictGame.participants.filter(p => p.won);
          if (winners.length > 0) {
            const marqueeData = winners.map(winner => {
              // Hash middle part of phone number for privacy
              const formatPhone = (phone) => {
                if (!phone) return '';
                // Remove country code if present
                const cleanPhone = phone.replace('+234', '');
                if (cleanPhone.length >= 6) {
                  const start = cleanPhone.substring(0, 3);
                  const end = cleanPhone.substring(cleanPhone.length - 2);
                  const middle = '*'.repeat(cleanPhone.length - 5);
                  return `${start}${middle}${end}`;
                }
                return phone;
              };

              return {
                displayText: `${winner.user.username || winner.user.name} (${formatPhone(winner.user.phone)}) won ₦${predictGame.potentialWin.toLocaleString()} on ${predictGame.fixture.homeTeam} vs ${predictGame.fixture.awayTeam}!`,
              };
            });
            setMarqueeWinners(marqueeData);
          }
        } else {
          // No match available today
          setTodaysMatch(null);
        }
      } catch (error) {
        console.error('Error fetching today\'s match:', error);
        // Check if it's a 404 (no match for today) vs actual error
        if (error.response?.status === 404) {
          setTodaysMatch(null);
          // Don't show error toast for no match available
        } else {
          toast.error('Unable to load today\'s match. Please try again later.');
        }
      } finally {
        setLoadingMatch(false);
      }
    };
    
    if (user) {
      fetchTodaysMatch();
    }
  }, [user]);

  useEffect(() => {
    if (todaysMatch) {
    setSelectedMatch(todaysMatch);
    }
  }, [todaysMatch]);

  useEffect(() => {
    if (showUsernameSetup && usernameInputRef.current) {
      const timer = setTimeout(() => {
        if (usernameInputRef.current) {
          usernameInputRef.current.focus();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showUsernameSetup]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (usernameCheckTimeoutRef.current) {
        clearTimeout(usernameCheckTimeoutRef.current);
      }
    };
  }, []);

  // Handlers
  const toggleBalanceVisibility = () => {
    setShowBalance(!showBalance);
  };

  const toggleStatsVisibility = () => {
    setShowStats(!showStats);
  };

  const handleCopyVirtualAccount = () => {
    navigator.clipboard.writeText(user?.user?.dedicatedAccountNumber);
    toast.success("Account number copied to clipboard!");
  };

  const handleWithdrawalClick = () => {
    toast.error("Withdrawal is currently disabled");
    setShowWithdrawalModal(false);
  };

  const handleWithdrawal = async () => {
    toast.error("Withdrawal is currently disabled");
    setShowWithdrawalModal(false);
    setWithdrawalAmount("");
  };

  // Function to check username availability
  const checkUsernameAvailability = async (usernameToCheck) => {
    if (usernameToCheck.length < 3) {
      setUsernameAvailability(null);
      return;
    }

    setIsCheckingUsername(true);
    try {
      const response = await api.get(`/auth/check-username?username=${encodeURIComponent(usernameToCheck)}`);
      setUsernameAvailability(response.data.available);
    } catch (error) {
      console.error('Error checking username availability:', error);
      setUsernameAvailability(null);
    } finally {
      setIsCheckingUsername(false);
    }
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "");
    setUsername(value);
    
    // Clear previous timeout
    if (usernameCheckTimeoutRef.current) {
      clearTimeout(usernameCheckTimeoutRef.current);
    }
    
    // Reset availability state
    setUsernameAvailability(null);
    
    // Set new timeout to check availability after user stops typing
    if (value.length >= 3) {
      usernameCheckTimeoutRef.current = setTimeout(() => {
        checkUsernameAvailability(value);
      }, 500); // 500ms delay
    }
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    if (value.length <= 10) {
      setPhoneNumber(value);
    }
  };

  const handleSetupUsername = async () => {
    if (username.length < 3) {
      toast.error("Username must be at least 3 characters long");
      return;
    }

    if (usernameAvailability === false) {
      toast.error("Username is already taken. Please choose another one.");
      return;
    }

    if (usernameAvailability === null) {
      toast.error("Please wait for username availability check to complete.");
      return;
    }

    if (needsPhoneNumber && phoneNumber.length !== 10) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    setIsSubmittingUsername(true);
    try {
      // Prepare request body
      const requestBody = { username: username };
      
      // Add phone number if needed
      if (needsPhoneNumber && phoneNumber) {
        requestBody.phone = `+234${phoneNumber}`;
      }

      // Make API call to add username (and phone if needed)
      const response = await api.post('/auth/add-username', requestBody);

      // Update user context with new username
      if (user && user.user) {
        user.user.username = response.data.username;
        if (needsPhoneNumber && phoneNumber) {
          user.user.phone = `+234${phoneNumber}`;
        }
      }

      // Set the username in local state
      setUserDisplayName(response.data.username);

      toast.success(response.data.message || "Profile updated successfully!");
      setShowUsernameSetup(false);
      
      // Reload the page to refresh all user data
      setTimeout(() => {
        window.location.reload();
      }, 1000); // Short delay to show the success message
    } catch (error) {
      console.error('Error adding username:', error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to update profile. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmittingUsername(false);
    }
  };

  const handleScoreChange = (team, value) => {
    // Only allow numbers and empty string
    if (value === "" || /^\d+$/.test(value)) {
      const numValue = parseInt(value);
      if (value === "" || (numValue >= 0 && numValue <= 99)) {
        if (team === "home") {
          setHomeScore(value);
        } else {
          setAwayScore(value);
        }
      }
    }
  };

  const handleSubmitPrediction = async () => {
    if (!showPredictionConfirmation) {
      // First click - show confirmation
      if (homeScore === "" || awayScore === "") {
        toast.error("Please enter both scores");
        return;
      }

      const hs = Number(homeScore);
      const as = Number(awayScore);
      if (!Number.isInteger(hs) || !Number.isInteger(as) || hs < 0 || as < 0) {
        toast.error("Scores must be non-negative integers");
        return;
      }

      // Check if user has already made a prediction for this game
      if (predictionStatus[selectedMatch.id] === "submitted" || predictionStatus[selectedMatch.id] === "won") {
        toast.error("You have already made a prediction for this match");
        return;
      }

      // Check wallet balance
      const userWalletBalance = user?.user?.walletBalance || 0;
      if (userWalletBalance < selectedMatch.entryFee) {
        toast.error(`Insufficient balance. You need ₦${selectedMatch.entryFee.toLocaleString()} to join this prediction.`);
        return;
      }

      // Check if predictions are still open (game starts in more than 1 hour)
      const matchDateTime = new Date(selectedMatch.date + 'T' + selectedMatch.time);
      const oneHourBeforeMatch = new Date(matchDateTime.getTime() - (60 * 60 * 1000));
      const now = new Date();

      if (now >= oneHourBeforeMatch) {
        toast.error("Predictions have closed for this match. Predictions close 1 hour before kickoff.");
        return;
      }

      setConfirmedHomeScore(homeScore);
      setConfirmedAwayScore(awayScore);
      setShowPredictionConfirmation(true);
    } else {
      // Second click - submit prediction to API
      setIsSubmittingUsername(true); // Using this for loading state
      
      try {
        const response = await api.post('/predict/join', {
          predictGameId: selectedMatch.id,
          homePrediction: parseInt(confirmedHomeScore),
          awayPrediction: parseInt(confirmedAwayScore)
        });

      // Set prediction status to submitted
      setPredictionStatus((prev) => ({
        ...prev,
        [selectedMatch.id]: "submitted",
      }));

        // Update user wallet balance in context
        if (user && user.user) {
          user.user.walletBalance = response.data.newWalletBalance;
        }

        // Refresh user stats from API
        try {
          const statsResponse = await api.get('/wallet/prediction-stats');
          const { totalPredictions, totalWins, totalWinnings, winRate } = statsResponse.data;
          
          setUserStats({
            totalWinnings: totalWinnings || 0,
            totalPredictions: totalPredictions || 0,
            wins: totalWins || 0,
            winRate: parseFloat(winRate) || 0,
          });
        } catch (statsError) {
          console.error('Error refreshing stats:', statsError);
          // Don't block the flow if stats refresh fails
        }

        toast.success(`Prediction submitted successfully! ₦${response.data.amountDeducted.toLocaleString()} deducted from your wallet.`);
      setShowPredictModal(false);
      setShowPredictionConfirmation(false);
      setHomeScore("");
      setAwayScore("");

        // Refresh today's match to update participant count
        const matchResponse = await api.get('/predict/today');
        if (matchResponse.data.predictGame) {
          const predictGame = matchResponse.data.predictGame;
          const transformedMatch = {
            id: predictGame._id,
            fixtureId: predictGame.fixture.fixtureId,
            homeTeam: predictGame.fixture.homeTeam,
            awayTeam: predictGame.fixture.awayTeam,
            homeLogo: predictGame.fixture.homeTeamEmblem,
            awayLogo: predictGame.fixture.awayTeamEmblem,
            date: new Date(predictGame.fixture.date).toISOString().split('T')[0],
            time: new Date(predictGame.fixture.date).toLocaleTimeString('en-GB', { 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: false 
            }),
            potentialWin: predictGame.potentialWin,
            entryFee: predictGame.participationFee,
            league: predictGame.fixture.league,
            leagueLogo: predictGame.fixture.leagueEmblem,
            currentParticipants: predictGame.participants.length,
            maxParticipants: predictGame.maxParticipants,
            slotsRemaining: predictGame.maxParticipants - predictGame.participants.length,
            played: predictGame.fixture.played,
            resultPrediction: predictGame.fixture.resultPrediction,
            overUnderPrediction: predictGame.fixture.overUnderPrediction,
            ggngPrediction: predictGame.fixture.ggngPrediction,
          };
          setTodaysMatch(transformedMatch);
        }
      } catch (error) {
        console.error('Error submitting prediction:', error);
        const errorMessage = error.response?.data?.message || error.message || "Failed to submit prediction. Please try again.";
        toast.error(errorMessage);
      } finally {
        setIsSubmittingUsername(false);
      }
    }
  };

  const handleEditPrediction = () => {
    setShowPredictionConfirmation(false);
  };

  const handlePredictClick = (match) => {
    setSelectedMatch(match);
    setShowPredictModal(true);
    setShowPredictionConfirmation(false);
    setHomeScore("");
    setAwayScore("");
  };

  // Get prediction status for a match
  const getPredictionStatus = (matchId) => {
    return predictionStatus[matchId];
  };

  // Confetti effect when win modal opens
  useEffect(() => {
    if (showWinModal) {
      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
      });

      setTimeout(() => {
        confetti({
          particleCount: 100,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
        });
      }, 250);

      setTimeout(() => {
        confetti({
          particleCount: 100,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
        });
      }, 400);
    }
  }, [showWinModal]);

  // Render main component
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50 pb-20">
      {/* Header */}
      <div className="bg-[#2C0053]  text-white p-6 pb-8 rounded-b-3xl shadow-lg"
       style={{
                    backgroundImage: `url(${Rbg})`,
                    backgroundSize: "contain",
                    backgroundPosition: "center",
                  }}
      >
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <img src={logo} alt="PredictWin" className="w-8 h-8" />
            <h1 className="text-[17px] font-bold">Predict & Win</h1>
          </div>

          {/* Header buttons */}
          <div className="flex items-center gap-2">
            {/* Stats button */}
            <button
              onClick={toggleStatsVisibility}
              className="bg-white/20 hover:bg-white/30 p-2 rounded-xl transition-colors"
            >
              <BarChart3 size={20} />
            </button>

            {/* Wallet button */}
            <button
              onClick={() => setShowWalletModal(true)}
              className="bg-white/20 hover:bg-white/30 p-2 rounded-xl transition-colors"
            >
              <Wallet size={20} />
            </button>
          </div>
        </div>

        <div className="text-center">
          <p className="text-sm opacity-90 mb-1 flex items-center justify-center gap-1">
            <TrendingUp size={14} /> Available Balance
          </p>
          <div className="flex items-center justify-center gap-2 mb-2">
            <h2 className="text-3xl font-semibold">
              {showBalance ? `₦${(user?.user?.walletBalance || 0).toLocaleString()}` : "xxxxxx"}
            </h2>
            <button
              onClick={toggleBalanceVisibility}
              className="text-white/80 hover:text-white transition-colors"
            >
              {showBalance ? <Eye size={18} /> : <EyeOff size={18} />}
            </button>
          </div>
          <button
            onClick={() => setShowLeaderboard(true)}
            className="inline-flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full text-xs transition-colors"
          >
            <Crown size={12} /> View Leaderboard
          </button>

          {/* Username display below leaderboard button - NOW SHOWS THE ACTUAL USERNAME */}
          <div className="mt-2">
            <p className="text-xs text-white/70">@{userDisplayName}</p>
          </div>
        </div>
      </div>

      {/* Marquee with relative positioning - NOW SHOWS ACTUAL WINNERS */}
      <Marquee marqueeWinners={marqueeWinners} />

      {/* User Stats Section - Conditionally rendered */}
      {showStats && (
        <div className="px-4 mb-8 md:flex justify-center items-center ">
          <div className="bg-white md:w-xl rounded-2xl p-4 border border-gray-200 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <BarChart3 className="text-[#2C0053]" size={20} />
              Your Predictions Stats
            </h3>

            {loadingStats ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2C0053] mx-auto mb-2"></div>
                <p className="text-sm text-gray-500">Loading stats...</p>
              </div>
            ) : (
              <>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-3 rounded-2xl border border-green-200">
                  <Trophy className="text-green-600 mx-auto mb-2" size={20} />
                  <p className="text-xs text-gray-600 mb-1">Total Winnings</p>
                  <p className="text-lg font-bold text-green-600">
                    ₦{userStats.totalWinnings.toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="text-center">
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-3 rounded-2xl border border-purple-200">
                  <Target className="text-[#2C0053] mx-auto mb-2" size={20} />
                  <p className="text-xs text-gray-600 mb-1">
                    Wins / Predictions
                  </p>
                  <p className="text-lg font-bold text-[#2C0053]">
                    {userStats.wins}/{userStats.totalPredictions}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-3 bg-gradient-to-br from-blue-50 to-cyan-50 p-3 rounded-2xl border border-blue-200">
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-gray-600">
                  Win Rate
                </span>
                <span className="text-sm font-bold text-blue-600">
                  {userStats.winRate}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${userStats.winRate}%` }}
                ></div>
              </div>
            </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Single Match Section */}
      <div className="md:flex justify-center items-center">
        <div className="px-4 relative  md:w-xl bottom-5">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Target className="text-[#2C0053]" size={20} />
            Today&apos;s Match
          </h3>

          {loadingMatch ? (
            <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
              <div className="flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2C0053] border-t-transparent mb-4"></div>
                <p className="text-gray-600 text-sm">Loading today&apos;s match...</p>
              </div>
            </div>
          ) : !todaysMatch ? (
            <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm text-center">
              <Target className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600 text-lg font-semibold mb-2">No Match Available</p>
              <p className="text-gray-500 text-sm">There is no predict and win game scheduled for today.</p>
            </div>
          ) : (
            <div className="bg-white  rounded-2xl p-4 border border-gray-200 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <img
                    src={todaysMatch.leagueLogo}
                    alt={todaysMatch.league}
                    className="w-5 h-5 rounded-full"
                  />
                  <span className="text-xs font-semibold text-gray-600">
                    {todaysMatch.league}
                  </span>
                </div>
                <div className="text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                  {todaysMatch.time}
                </div>
              </div>

              <div className="flex items-center justify-between mb-4">
                <div className="text-center flex-1">
                  <img
                    src={todaysMatch.homeLogo}
                    alt={todaysMatch.homeTeam}
                    className="w-12 h-12 rounded-2xl mx-auto mb-2 border-2 border-white shadow-md"
                  />
                  <p className="text-sm font-semibold text-gray-900">
                    {todaysMatch.homeTeam}
                  </p>
                </div>

                <div className="mx-4">
                  <div className="bg-gradient-to-br from-[#2C0053] to-[#4a0080] text-white px-3 py-2 rounded-xl shadow-lg">
                    <span className="text-xs font-bold">VS</span>
                  </div>
                </div>

                <div className="text-center flex-1">
                  <img
                    src={todaysMatch.awayLogo}
                    alt={todaysMatch.awayTeam}
                    className="w-12 h-12 rounded-2xl mx-auto mb-2 border-2 border-white shadow-md"
                  />
                  <p className="text-sm font-semibold text-gray-900">
                    {todaysMatch.awayTeam}
                  </p>
                </div>
              </div>

              {/* Only show entry fee, potential win, and slots if prediction is not won or lost */}
              {getPredictionStatus(todaysMatch.id) !== "won" && getPredictionStatus(todaysMatch.id) !== "lost" && (
                <>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">Entry Fee</p>
                      <p className="text-sm font-semibold text-red-600">
                        ₦{todaysMatch.entryFee.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">
                        Potential Win
                      </p>
                      <p className="text-sm font-semibold text-green-600">
                        ₦{todaysMatch.potentialWin.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <User size={12} />
                      {todaysMatch.currentParticipants}/
                      {todaysMatch.maxParticipants} slots filled
                      <span className="text-green-600 font-semibold ml-1">
                        ({slotsRemaining} remaining)
                      </span>
                    </div>
                    <PredictionButton
                      status={getPredictionStatus(todaysMatch.id)}
                      onClick={() => handlePredictClick(todaysMatch)}
                    />
                  </div>
                </>
              )}

              {/* Show win celebration message if prediction is won */}
              {getPredictionStatus(todaysMatch.id) === "won" && (
                <div className="text-center py-4">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-4 border border-green-200 mb-4">
                    <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
                      <Trophy size={20} />
                      <span className="font-semibold">You Won! 🎉</span>
                    </div>
                    <p className="text-sm text-green-600">
                      Congratulations! Your prediction was correct
                    </p>
                  </div>
                  <PredictionButton
                    status={getPredictionStatus(todaysMatch.id)}
                    onClick={() => handlePredictClick(todaysMatch)}
                  />
                </div>
              )}

              {/* Show lost message if prediction is lost */}
              {getPredictionStatus(todaysMatch.id) === "lost" && (
                <div className="text-center py-4">
                  <div className="bg-gradient-to-r from-red-50 to-rose-50 rounded-2xl p-4 border border-red-200 mb-4">
                    <div className="flex items-center justify-center gap-2 text-red-600 mb-2">
                      <X size={20} />
                      <span className="font-semibold">Better Luck Next Time</span>
                    </div>
                    <p className="text-sm text-red-600">
                      Your prediction was incorrect. Try again tomorrow!
                    </p>
                  </div>
                  <PredictionButton
                    status={getPredictionStatus(todaysMatch.id)}
                    onClick={() => handlePredictClick(todaysMatch)}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <UsernameSetupModal
        showUsernameSetup={showUsernameSetup}
        username={username}
        phoneNumber={phoneNumber}
        handleUsernameChange={handleUsernameChange}
        handlePhoneNumberChange={handlePhoneNumberChange}
        handleSetupUsername={handleSetupUsername}
        isSubmittingUsername={isSubmittingUsername}
        isCheckingUsername={isCheckingUsername}
        usernameAvailability={usernameAvailability}
        usernameInputRef={usernameInputRef}
        needsPhoneNumber={needsPhoneNumber}
      />

      <WalletModal
        showWalletModal={showWalletModal}
        setShowWalletModal={setShowWalletModal}
        showBalance={showBalance}
        toggleBalanceVisibility={toggleBalanceVisibility}
        handleCopyVirtualAccount={handleCopyVirtualAccount}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filteredTransactions={filteredTransactions}
        user={user}
        loadingTransactions={loadingTransactions}
        onWithdrawClick={handleWithdrawalClick}
      />

      <WithdrawalModal
        showWithdrawalModal={showWithdrawalModal}
        setShowWithdrawalModal={setShowWithdrawalModal}
        withdrawalAmount={withdrawalAmount}
        setWithdrawalAmount={setWithdrawalAmount}
        handleWithdrawal={handleWithdrawal}
        isWithdrawing={isWithdrawing}
        user={user}
      />

      <PredictModal
        showPredictModal={showPredictModal}
        setShowPredictModal={setShowPredictModal}
        selectedMatch={selectedMatch}
        showPredictionConfirmation={showPredictionConfirmation}
        homeScore={homeScore}
        awayScore={awayScore}
        confirmedHomeScore={confirmedHomeScore}
        confirmedAwayScore={confirmedAwayScore}
        handleScoreChange={handleScoreChange}
        handleSubmitPrediction={handleSubmitPrediction}
        handleEditPrediction={handleEditPrediction}
        predictionStatus={predictionStatus}
      />

      <LeaderboardModal
        showLeaderboard={showLeaderboard}
        setShowLeaderboard={setShowLeaderboard}
        leaderboardData={leaderboardData}
        pastWinners={pastWinners}
        loadingLeaderboard={loadingLeaderboard}
      />

      <WinModal
        showWinModal={showWinModal}
        setShowWinModal={setShowWinModal}
        winAmount={winAmount}
      />

      <BottomNavbar />
    </div>
  );
}
